import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import Markdown from "src/components/Markdown";
import Button from "src/components/Button/Button";
import { darkCardStyle } from "src/GlobalStyle";
import { extractTimeFromDateTime, formatTimeString } from "src/utils/timeUtils";
import ResponsiveImage from "src/components/ImageWrapper/ResponsiveImage";

const AvailablePromoCard = ({ promo }) => {
  const { t, i18n } = useTranslation();

  const imageUrl = promo.promo_details?.image_url;

  const description = promo.promo_details?.description
    ? promo.promo_details.description.replace("{amount}", promo.pm_token_reward || "")
    : "";

  const buttonLink = promo.promo_details?.button_link;

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const locale = i18n.language === "fr" ? "fr-CA" : "en-US";
    const datePart = date.toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" });
    const timePart = formatTimeString(date, i18n.language);
    return `${datePart}, ${timePart}`;
  };

  const translateSchedule = (schedule) => {
    if (!schedule) return schedule;

    const translations = {
      "All 7 days": i18n.language === "fr" ? "Les 7 jours" : "All 7 days",
      "Weekdays": i18n.language === "fr" ? "Jours de semaine" : "Weekdays",
      "Weekends": i18n.language === "fr" ? "Fins de semaine" : "Weekends",
      "Monday": i18n.language === "fr" ? "Lundi" : "Monday",
      "Tuesday": i18n.language === "fr" ? "Mardi" : "Tuesday",
      "Wednesday": i18n.language === "fr" ? "Mercredi" : "Wednesday",
      "Thursday": i18n.language === "fr" ? "Jeudi" : "Thursday",
      "Friday": i18n.language === "fr" ? "Vendredi" : "Friday",
      "Saturday": i18n.language === "fr" ? "Samedi" : "Saturday",
      "Sunday": i18n.language === "fr" ? "Dimanche" : "Sunday",
    };

    return String(schedule)
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => translations[part] || part)
      .join(", ");
  };

  const showDates = promo.promo_details?.show_dates;

  const alt = promo.promo_details?.title || promo.parsed_promo_name || promo.pm_name;

  return (
    <PromoCard>
      {imageUrl && buttonLink ? (
        <PromoImageLink to={buttonLink}>
          <ResponsiveImage src={imageUrl} alt={alt} />
        </PromoImageLink>
      ) : imageUrl ? (
        <ResponsiveImage src={imageUrl} alt={alt} />
      ) : null}
      <PromoContent>
        <h4>{promo.promo_details?.title || promo.parsed_promo_name || promo.pm_name}</h4>
        {description && (
          <PromoDescription>
            <Markdown>{description}</Markdown>
          </PromoDescription>
        )}
        {showDates === "times" && (
          <>
            <PromoDates>
              <small><strong>{t("myAccount.availablePromos.validFromLabel")}</strong></small>
              <small>{extractTimeFromDateTime(promo.pm_date_start, i18n.language)}</small>
            </PromoDates>
            <PromoDates>
              <small><strong>{t("myAccount.availablePromos.validUntilLabel")}</strong></small>
              <small>{extractTimeFromDateTime(promo.pm_date_finish, i18n.language)}</small>
            </PromoDates>
          </>
        )}
        {showDates === true && (
          <>
            <PromoDates>
              <small><strong>{t("myAccount.availablePromos.validFromLabel")}</strong></small>
              <small>{formatDateTime(promo.pm_date_start)}</small>
            </PromoDates>
            <PromoDates>
              <small><strong>{t("myAccount.availablePromos.validUntilLabel")}</strong></small>
              <small>{formatDateTime(promo.pm_date_finish)}</small>
            </PromoDates>
          </>
        )}
        {promo.pm_schedule_weekday && (
          <PromoSchedule>
            <small><strong>{t("myAccount.availablePromos.scheduleLabel")}</strong></small>
            <small>{translateSchedule(promo.pm_schedule_weekday)}</small>
          </PromoSchedule>
        )}
        {buttonLink && (
          <Button
            href={buttonLink}
            label={t("myAccount.availablePromos.viewDetails")}
            size="small"
            fluid
            className="mt-3"
          />
        )}
      </PromoContent>
    </PromoCard>
  );
};

const PromoCard = styled.div`
  ${darkCardStyle}
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s;
  &:hover {
    transform: translateY(-5px);
  }
`;

const PromoImageLink = styled(({ to, children, ...props }) => {
  const isExternalLink = to && to.startsWith("http");
  return isExternalLink ? (
    <a href={to} {...props}>{children}</a>
  ) : (
    <Link to={to} {...props}>{children}</Link>
  );
})`
  display: block;
  width: 100%;
  overflow: hidden;
  cursor: pointer;
  &:hover img {
    transform: scale(1.05);
  }
`;

const PromoContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  h4 {
    color: var(--white);
    margin-bottom: 1rem;
    font-size: 1.2rem;
  }
`;

const PromoDescription = styled.div`
  color: var(--mutedwhite);
  margin-bottom: 1rem;
  flex: 1;
  p {
    margin-bottom: 0.5rem;
    &:last-child { margin-bottom: 0; }
  }
  strong {
    color: var(--white);
  }
`;

const PromoDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 0.5rem;
  color: var(--mutedwhite);
  margin-bottom: 0.25rem;
  small:first-child {
    flex-shrink: 0;
  }
  small:last-child {
    text-align: right;
  }
`;

const PromoDates = PromoDetailRow;
const PromoSchedule = PromoDetailRow;

export default AvailablePromoCard;
